import Button from '@repo/ui/button'
import { UploadIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DragEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { createEndOfJourneyScans } from '../../../api/end-of-journey-scans/create-end-of-journey-scans'
import { getEndOfJourneyScanUploadLimits } from '../../../api/end-of-journey-scans/get-end-of-journey-scan-upload-limits'
import { uploadFileSecurely, UploadPurpose } from '../../../api/media/secure-upload'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  extractAllErrorMessages,
  showAxiosError
} from '../../../lib/utils/functions/misc.functions'
import { isPdf, isReadingPaused } from './end-of-journey-scan.utils'

// Files sent to the storage at the same time
const UPLOAD_CONCURRENCY = 4

// The files of a dropped folder (and its sub-folders)
const readEntryFiles = async (entry: FileSystemEntry): Promise<File[]> => {
  // Hidden files and folders (.DS_Store, ...) are skipped silently
  if (entry.name.startsWith('.')) return []
  if (entry.isFile)
    return new Promise((resolve) => (entry as FileSystemFileEntry).file((file) => resolve([file])))
  if (!entry.isDirectory) return []

  const reader = (entry as FileSystemDirectoryEntry).createReader()
  const entries: FileSystemEntry[] = []
  // readEntries returns the entries in chunks until an empty one
  for (;;) {
    const chunk = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject)
    )
    if (chunk.length === 0) break
    entries.push(...chunk)
  }
  return (await Promise.all(entries.map(readEntryFiles))).flat()
}

const EndOfJourneyScanUpload = () => {
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number }>()

  const {
    data: limits,
    error: limitsError,
    isFetching: isLimitsFetching,
    refetch: refetchLimits
  } = useQuery({
    queryKey: ['end-of-journey-scan-upload-limits'],
    queryFn: getEndOfJourneyScanUploadLimits
  })
  // The last failure stays on screen while "Réessayer" loads the limits again: react-query clears
  // the error of a query without data as soon as it is fetched again
  const [lastLimitsError, setLastLimitsError] = useState<Error>()
  useEffect(() => {
    if (limitsError) setLastLimitsError(limitsError)
  }, [limitsError])
  const shownLimitsError = limitsError ?? lastLimitsError
  const maxFileMb = limits ? limits.maxFileBytes / (1024 * 1024) : null

  // Keeps the PDFs within the backend's limits, lists the others
  const selectFiles = (selected: File[]) => {
    if (!limits) return
    const nextErrors: string[] = []
    const accepted: File[] = []
    for (const file of selected) {
      if (!isPdf(file))
        nextErrors.push(t('end.of.journey.scans.upload.error.not.pdf', { fileName: file.name }))
      else if (file.size > limits.maxFileBytes)
        nextErrors.push(
          t('end.of.journey.scans.upload.error.too.large', { fileName: file.name, maxFileMb })
        )
      else accepted.push(file)
    }
    if (accepted.length > limits.maxFiles)
      nextErrors.push(
        t('end.of.journey.scans.upload.error.too.many', {
          number: accepted.length,
          maxFiles: limits.maxFiles
        })
      )
    setErrors(nextErrors)
    setFiles(accepted.length > limits.maxFiles ? [] : accepted)
  }

  const reset = () => {
    setFiles([])
    setErrors([])
    setProgress(undefined)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Each PDF through the secure upload (virus scan), a few at a time, then registered at once
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const uploadIds: string[] = []
      const failed: string[] = []
      let next = 0
      setProgress({ done: 0, total: files.length })
      const worker = async () => {
        while (next < files.length) {
          const file = files[next++]
          try {
            uploadIds.push(
              await uploadFileSecurely({ file, purpose: UploadPurpose.END_OF_JOURNEY_SCAN })
            )
          } catch {
            failed.push(file.name)
          }
          setProgress((current) => current && { ...current, done: current.done + 1 })
        }
      }
      await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker))

      const result = uploadIds.length ? await createEndOfJourneyScans({ uploadIds }) : null
      return { count: result?.count ?? 0, readingStatus: result?.readingStatus, failed }
    },
    onError: (error) => {
      setProgress(undefined)
      showAxiosError(error)
    },
    onSuccess: ({ count, readingStatus, failed }) => {
      reset()
      // Received in any case; while the reading is paused they wait until it resumes
      if (count && isReadingPaused(readingStatus))
        toast.warning(t('end.of.journey.scans.upload.success.paused', { number: count }))
      else if (count) toast.success(t('end.of.journey.scans.upload.success', { number: count }))
      if (failed.length) {
        setErrors(
          failed.map((fileName) => t('end.of.journey.scans.upload.error.failed', { fileName }))
        )
        toast.error(t('end.of.journey.scans.upload.failed', { number: failed.length }))
      }
      queryClient.invalidateQueries({ queryKey: ['end-of-journey-scans'] })
    }
  })

  const onDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (isPending) return
    const entries = Array.from(event.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => !!entry)
    const dropped = entries.length
      ? (await Promise.all(entries.map(readEntryFiles))).flat()
      : Array.from(event.dataTransfer.files)
    selectFiles(dropped)
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">
        {limits
          ? t('end.of.journey.scans.upload.help.limits', { maxFiles: limits.maxFiles, maxFileMb })
          : t('end.of.journey.scans.upload.help')}
      </p>
      <div
        className={`flex flex-col items-center gap-2 rounded border border-dashed p-6 ${
          isDragging ? 'border-primary bg-primary-light' : 'border-grey-light'
        }`}
        onDragOver={(event) => {
          event.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          onChange={(event) => selectFiles(Array.from(event.target.files ?? []))}
        />
        <span className="text-sm">{t('end.of.journey.scans.upload.drop.zone')}</span>
        <Button
          variant="outline"
          icon={UploadIcon}
          label={t('end.of.journey.scans.upload.choose.files')}
          disabled={!limits || isPending}
          onClick={() => fileInputRef.current?.click()}
        />
        {/* Without the backend's limits no file can be checked, so none is taken: says why */}
        {shownLimitsError && !limits && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-red">
              {t('end.of.journey.scans.upload.error.limits', {
                message: extractAllErrorMessages(shownLimitsError).join(' ')
              })}
            </span>
            <Button
              size="xsmall"
              variant="outline"
              label={t('end.of.journey.scans.load.retry')}
              loading={isLimitsFetching}
              onClick={() => refetchLimits()}
            />
          </div>
        )}
        {files.length > 0 && (
          <span className="text-sm">
            {t('end.of.journey.scans.upload.ready', { number: files.length })}
          </span>
        )}
        {progress && (
          <span className="text-sm">
            {t('end.of.journey.scans.upload.progress', {
              done: progress.done,
              total: progress.total
            })}
          </span>
        )}
      </div>
      {errors.length > 0 && (
        <ul className="max-h-[8.5rem] overflow-y-auto rounded border border-red-light-active px-3 py-2 text-sm text-red">
          {errors.map((error) => (
            <li key={error} className="truncate" title={error}>
              {error}
            </li>
          ))}
        </ul>
      )}
      <div className="flex justify-end gap-2">
        {(files.length > 0 || errors.length > 0) && !isPending && (
          <Button variant="outline" label={t('end.of.journey.scans.cancel')} onClick={reset} />
        )}
        <Button
          label={t('end.of.journey.scans.send')}
          loading={isPending}
          disabled={files.length === 0}
          onClick={() => mutate()}
        />
      </div>
    </div>
  )
}

export default EndOfJourneyScanUpload
